import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useQuery } from "@tanstack/react-query";
import { api, formatPrice, imageUrl, uiText } from "@/lib/api";
import { Trash2, Minus, Plus } from "lucide-react";

export default function CartPage() {
    const { items, updateQty, removeItem, clearCart, totalPrice } = useCart();
    const { data: settings } = useQuery({ queryKey: ["settings-cart"], queryFn: async () => (await api.get("/settings")).data });
    const { data: products = [] } = useQuery({ queryKey: ["products-cart"], queryFn: async () => (await api.get("/products")).data });
    const stockMap = Object.fromEntries(products.map((p) => [p.id, p.stock ?? 0]));

    if (items.length === 0) {
        return (
            <div className="mx-auto max-w-3xl px-4 sm:px-8 py-24 text-center">
                <h1 className="font-display text-3xl text-ink mb-3">{uiText(settings, "cart_empty_title")}</h1>
                <p className="text-sm text-ink/60 mb-8">{uiText(settings, "cart_empty_sub")}</p>
                <Link to="/tienda" className="btn-ink">Ir a la tienda</Link>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-10">
            <h1 className="font-display text-4xl text-ink mb-8">Tu carrito</h1>
            <div className="grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 divide-y divide-sandLight">
                    {items.map((it) => (
                        <div key={it.lineId} className="flex gap-4 py-6" data-testid={`cart-item-${it.productId}`}>
                            <div className="relative w-24 aspect-[4/5] rounded-md overflow-hidden bg-sandLight shrink-0">
                                {it.image && <img src={imageUrl(it.image)} alt={it.name} className="absolute inset-0 w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-eyebrow">{it.brand}</p>
                                        <p className="text-sm text-ink mt-1">{it.name}</p>
                                        <p className="text-xs text-ink/60 mt-1">{it.size && `Talla: ${it.size}`} {it.color && `· Color: ${it.color}`}</p>
                                    </div>
                                    <button onClick={() => removeItem(it.lineId)} className="text-ink/40 hover:text-terracotta"><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <div className="flex items-center justify-between mt-4">
                                    <div className="inline-flex items-center border border-sandLight rounded-full">
                                        <button onClick={() => updateQty(it.lineId, it.quantity - 1)} className="w-8 h-8 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                                        <span className="w-7 text-center text-xs">{it.quantity}</span>
                                        <button onClick={() => updateQty(it.lineId, it.quantity + 1)} disabled={stockMap[it.productId] !== undefined && it.quantity >= stockMap[it.productId]} data-testid={`cart-plus-${it.productId}`} className="w-8 h-8 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"><Plus className="w-3 h-3" /></button>
                                    </div>
                                    <p className="text-sm text-ink">{formatPrice(it.price * it.quantity)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="pt-6">
                        <button onClick={clearCart} className="text-xs uppercase tracking-wide text-ink/50 hover:text-terracotta underline underline-offset-4">Vaciar carrito</button>
                    </div>
                </div>
                <div className="bg-white border border-sandLight rounded-lg p-6 h-fit sticky top-24">
                    <p className="text-eyebrow mb-4">Resumen del pedido</p>
                    <div className="flex justify-between text-sm text-ink/70 mb-2"><span>Subtotal</span><span>{formatPrice(totalPrice)}</span></div>
                    <p className="text-xs text-ink/50 mb-4">El envío se calcula en el checkout.</p>
                    <div className="flex justify-between text-base font-medium text-ink border-t border-sandLight pt-4 mb-6"><span>TOTAL</span><span>{formatPrice(totalPrice)}</span></div>
                    <Link to="/checkout" data-testid="btn-proceed-checkout" className="btn-ink w-full inline-flex items-center justify-center">Ir a Pagar</Link>
                    <Link to="/tienda" className="block text-center text-xs uppercase tracking-wide text-ink/60 hover:text-ink mt-4">Seguir comprando</Link>
                </div>
            </div>
        </div>
    );
}
